import { db } from '../db/database';
import { enqueueChange } from '../db/outbox.repo';
import type { SyncEntityType } from '../db/database';
import { completionId, taskTagId } from '../../../entities/task/model/task.types';
import type {
  ChecklistItemEntity,
  CompletionEntity,
  ProjectEntity,
  TagEntity,
  TaskEntity,
  TaskTagEntity,
} from '../../../entities/task/model/task.types';
import type { HabitCompletionEntity, HabitEntity, HabitFrequency } from '../../../entities/habit/model/habit.types';
import { asHabitFrequency, habitCompletionId } from '../../../entities/habit/model/habit.types';
import type { TaskPriority } from '../../../shared/lib/datetime';

export interface LocalMutationContext {
  userId: string;
}

export interface TaskMutationPatch {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  due_date?: string | null;
  due_time?: string | null;
  duration_minutes?: number | null;
  all_day?: boolean;
  is_idea?: boolean;
  completed?: boolean;
  completed_at?: string | null;
  archived_at?: string | null;
  recurrence_rule?: string | null;
  project_id?: string | null;
}

export interface CreateTaskInput {
  id: string;
  title: string;
  description?: string;
  priority?: TaskPriority;
  due_date?: string | null;
  due_time?: string | null;
  duration_minutes?: number | null;
  all_day?: boolean;
  is_idea?: boolean;
  recurrence_rule?: string | null;
  project_id?: string | null;
}

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Единая точка локальных изменений:
 * 1) мгновенная запись в Dexie с dirty=true;
 * 2) запись в outbox с client_op_id;
 * 3) фоновый push позже (syncEngine).
 */
export async function applyLocalTaskMutation(
  ctx: LocalMutationContext,
  taskId: string,
  patch: TaskMutationPatch,
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const existing = await db.tasks.get(taskId);
  const deleted = options.deleted === true;

  const base = existing ?? {
    id: taskId,
    user_id: ctx.userId,
    title: '',
    description: '',
    priority: 'medium' as const,
    due_date: null,
    due_time: null,
    duration_minutes: null,
    all_day: false,
    is_idea: false,
    completed: false,
    completed_at: null,
    archived_at: null,
    recurrence_rule: null,
    project_id: null,
    created_at: updatedAt,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next = {
    ...base,
    ...patch,
    id: taskId,
    user_id: ctx.userId,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    title: next.title,
    description: next.description,
    priority: next.priority,
    due_date: next.due_date,
    due_time: next.due_time,
    duration_minutes: next.duration_minutes,
    all_day: next.all_day,
    is_idea: next.is_idea,
    completed: next.completed,
    completed_at: next.completed_at,
    archived_at: next.archived_at,
    recurrence_rule: next.recurrence_rule,
    project_id: next.project_id,
    created_at: next.created_at,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.tasks, db.outbox, async () => {
    await db.tasks.put(next);
    await enqueueChange({
      entity_type: 'task',
      entity_id: taskId,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

export async function applyLocalTagMutation(
  ctx: LocalMutationContext,
  tagId: string,
  patch: { name?: string; color?: string },
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const existing = await db.tags.get(tagId);
  const deleted = options.deleted === true;

  const base = existing ?? {
    id: tagId,
    user_id: ctx.userId,
    name: '',
    color: '#38bdf8',
    created_at: updatedAt,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next = {
    ...base,
    ...patch,
    id: tagId,
    user_id: ctx.userId,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    name: next.name,
    color: next.color,
    created_at: next.created_at,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.tags, db.outbox, async () => {
    await db.tags.put(next);
    await enqueueChange({
      entity_type: 'tag',
      entity_id: tagId,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

export async function applyLocalProjectMutation(
  ctx: LocalMutationContext,
  projectId: string,
  patch: { name?: string; color?: string },
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const existing = await db.projects.get(projectId);
  const deleted = options.deleted === true;

  const base = existing ?? {
    id: projectId,
    user_id: ctx.userId,
    name: '',
    color: '#38bdf8',
    created_at: updatedAt,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next = {
    ...base,
    ...patch,
    id: projectId,
    user_id: ctx.userId,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    name: next.name,
    color: next.color,
    created_at: next.created_at,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.projects, db.outbox, async () => {
    await db.projects.put(next);
    await enqueueChange({
      entity_type: 'project',
      entity_id: projectId,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

export async function applyLocalChecklistItemMutation(
  ctx: LocalMutationContext,
  itemId: string,
  patch: {
    task_id?: string;
    text?: string;
    done?: boolean;
    sort_order?: number;
  },
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const existing = await db.checklist_items.get(itemId);
  const deleted = options.deleted === true;

  const base: ChecklistItemEntity = existing ?? {
    id: itemId,
    task_id: '',
    user_id: ctx.userId,
    text: '',
    done: false,
    sort_order: 0,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next: ChecklistItemEntity = {
    ...base,
    ...patch,
    id: itemId,
    user_id: ctx.userId,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    id: next.id,
    task_id: next.task_id,
    text: next.text,
    done: next.done,
    sort_order: next.sort_order,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.checklist_items, db.outbox, async () => {
    await db.checklist_items.put(next);
    await enqueueChange({
      entity_type: 'checklist_item',
      entity_id: itemId,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

export async function applyLocalTaskTagMutation(
  ctx: LocalMutationContext,
  taskId: string,
  tagId: string,
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const id = taskTagId(taskId, tagId);
  const existing = await db.task_tags.get(id);
  const deleted = options.deleted === true;

  const base: TaskTagEntity = existing ?? {
    id,
    task_id: taskId,
    tag_id: tagId,
    user_id: ctx.userId,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next: TaskTagEntity = {
    ...base,
    task_id: taskId,
    tag_id: tagId,
    user_id: ctx.userId,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    task_id: taskId,
    tag_id: tagId,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.task_tags, db.outbox, async () => {
    await db.task_tags.put(next);
    await enqueueChange({
      entity_type: 'task_tag',
      entity_id: id,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

export async function applyLocalCompletionMutation(
  ctx: LocalMutationContext,
  taskId: string,
  occurrenceDate: string,
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const id = completionId(taskId, occurrenceDate);
  const existing = await db.completions.get(id);
  const deleted = options.deleted === true;

  const base: CompletionEntity = existing ?? {
    id,
    task_id: taskId,
    occurrence_date: occurrenceDate,
    user_id: ctx.userId,
    created_at: updatedAt,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next: CompletionEntity = {
    ...base,
    task_id: taskId,
    occurrence_date: occurrenceDate,
    user_id: ctx.userId,
    created_at: deleted ? base.created_at : updatedAt,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    task_id: taskId,
    occurrence_date: occurrenceDate,
    created_at: next.created_at,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.completions, db.outbox, async () => {
    await db.completions.put(next);
    await enqueueChange({
      entity_type: 'completion',
      entity_id: id,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

export async function createTaskLocally(
  ctx: LocalMutationContext,
  input: CreateTaskInput,
): Promise<void> {
  await applyLocalTaskMutation(ctx, input.id, {
    title: input.title.trim(),
    description: input.description ?? '',
    priority: input.priority ?? 'medium',
    due_date: input.due_date ?? null,
    due_time: input.due_time ?? null,
    duration_minutes: input.duration_minutes ?? null,
    all_day: input.all_day ?? false,
    is_idea: input.is_idea ?? false,
    completed: false,
    completed_at: null,
    archived_at: null,
    recurrence_rule: input.recurrence_rule ?? null,
    project_id: input.project_id ?? null,
  });
}

/** Жёсткое удаление задачи: связанные сущности + tombstone задачи. */
export async function deleteTaskPermanently(
  ctx: LocalMutationContext,
  taskId: string,
): Promise<void> {
  const checklistItems = await db.checklist_items.where('task_id').equals(taskId).toArray();
  for (const item of checklistItems) {
    await applyLocalChecklistItemMutation(ctx, item.id, { task_id: taskId }, { deleted: true });
  }

  const taskTags = await db.task_tags.where('task_id').equals(taskId).toArray();
  for (const link of taskTags) {
    await applyLocalTaskTagMutation(ctx, taskId, link.tag_id, { deleted: true });
  }

  const completions = await db.completions.where('task_id').equals(taskId).toArray();
  for (const completion of completions) {
    await applyLocalCompletionMutation(ctx, taskId, completion.occurrence_date, {
      deleted: true,
    });
  }

  await applyLocalTaskMutation(ctx, taskId, {}, { deleted: true });
}

/** Удаление тега: снимаем связи с задач, сам тег — tombstone. */
export async function deleteTagPermanently(
  ctx: LocalMutationContext,
  tagId: string,
): Promise<void> {
  const links = await db.task_tags.where('tag_id').equals(tagId).toArray();
  for (const link of links) {
    await applyLocalTaskTagMutation(ctx, link.task_id, tagId, { deleted: true });
  }
  await applyLocalTagMutation(ctx, tagId, {}, { deleted: true });
}

/** Удаление проекта: tasks.project_id → null, сам проект — tombstone. */
export async function deleteProjectPermanently(
  ctx: LocalMutationContext,
  projectId: string,
): Promise<void> {
  const tasks = await db.tasks.where('project_id').equals(projectId).toArray();
  for (const task of tasks) {
    await applyLocalTaskMutation(ctx, task.id, { project_id: null });
  }
  await applyLocalProjectMutation(ctx, projectId, {}, { deleted: true });
}

export interface HabitMutationPatch {
  name?: string;
  description?: string;
  color?: string;
  frequency?: HabitFrequency;
  start_date?: string;
  paused_at?: string | null;
}

/**
 * Локальная правка привычки: Dexie dirty=true + outbox.
 * Frequency хранится объектом; в payload уходит как объект (сервер может JSON-сериализовать).
 */
export async function applyLocalHabitMutation(
  ctx: LocalMutationContext,
  habitId: string,
  patch: HabitMutationPatch,
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const existing = await db.habits.get(habitId);
  const deleted = options.deleted === true;

  const base: HabitEntity = existing ?? {
    id: habitId,
    user_id: ctx.userId,
    name: '',
    description: '',
    color: '#38bdf8',
    frequency: { type: 'interval', interval: 1 },
    start_date: updatedAt.slice(0, 10),
    paused_at: null,
    created_at: updatedAt,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next: HabitEntity = {
    ...base,
    ...patch,
    frequency:
      patch.frequency !== undefined ? asHabitFrequency(patch.frequency) : base.frequency,
    id: habitId,
    user_id: ctx.userId,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    name: next.name,
    description: next.description,
    color: next.color,
    frequency: next.frequency,
    start_date: next.start_date,
    paused_at: next.paused_at,
    created_at: next.created_at,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.habits, db.outbox, async () => {
    await db.habits.put(next);
    await enqueueChange({
      entity_type: 'habit',
      entity_id: habitId,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

export async function applyLocalHabitCompletionMutation(
  ctx: LocalMutationContext,
  habitId: string,
  completionDate: string,
  options: { deleted?: boolean } = {},
): Promise<void> {
  const updatedAt = nowIso();
  const id = habitCompletionId(habitId, completionDate);
  const existing = await db.habit_completions.get(id);
  const deleted = options.deleted === true;

  const base: HabitCompletionEntity = existing ?? {
    id,
    habit_id: habitId,
    completion_date: completionDate,
    user_id: ctx.userId,
    created_at: updatedAt,
    updated_at: updatedAt,
    deleted_at: null,
    server_revision: null,
    dirty: true,
  };

  const next: HabitCompletionEntity = {
    ...base,
    habit_id: habitId,
    completion_date: completionDate,
    user_id: ctx.userId,
    created_at: deleted ? base.created_at : updatedAt,
    updated_at: updatedAt,
    deleted_at: deleted ? updatedAt : null,
    dirty: true,
  };

  const payload = {
    habit_id: habitId,
    completion_date: completionDate,
    created_at: next.created_at,
    updated_at: updatedAt,
    deleted_at: next.deleted_at,
  };

  await db.transaction('rw', db.habit_completions, db.outbox, async () => {
    await db.habit_completions.put(next);
    await enqueueChange({
      entity_type: 'habit_completion',
      entity_id: id,
      deleted,
      payload,
      updated_at: updatedAt,
    });
  });
}

/** Удаление привычки: tombstone привычки + все её completions. */
export async function deleteHabitPermanently(
  ctx: LocalMutationContext,
  habitId: string,
): Promise<void> {
  const completions = await db.habit_completions.where('habit_id').equals(habitId).toArray();
  for (const completion of completions) {
    await applyLocalHabitCompletionMutation(ctx, habitId, completion.completion_date, {
      deleted: true,
    });
  }
  await applyLocalHabitMutation(ctx, habitId, {}, { deleted: true });
}

export type { SyncEntityType, TaskEntity, TagEntity, ProjectEntity, HabitEntity };
